import { FormEvent, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { MessageSquareText } from "lucide-react";
import {
  CHAT_THREAD_SUBJECT_MAX_LENGTH,
  CHAT_THREAD_SUBJECT_MIN_LENGTH,
} from "@cliently/types";
import { Button, Label } from "@cliently/ui";
import { LottieIllustration } from "../components/LottieIllustration";
import { usePortalConversations } from "../hooks/usePortalChat";
import {
  createPortalConversation,
  postPortalConversationsSeenAll,
} from "../lib/chatApi";
import { ALL_CLIENTS_KEY, useOrgStore } from "../stores/org.store";
import { useOrganizations } from "../hooks/use-organizations";
import ghibliLottie from "../../../pro/src/assets/lottie/ghibli.json";

const AUTO_ARCHIVE_AFTER_MS = 7 * 24 * 60 * 60 * 1000;
const WARNING_THRESHOLD_MS = 2 * 24 * 60 * 60 * 1000;



const getRemainingBeforeAutoArchive = (updatedAt: string): number => {
  const remaining = AUTO_ARCHIVE_AFTER_MS - (Date.now() - new Date(updatedAt).getTime());
  return Math.max(0, remaining);
}



const formatRemainingBeforeAutoArchive = (remainingMs: number): string => {
  const totalMinutes = Math.ceil(remainingMs / (60 * 1000));
  const days = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);

  if (days > 0) {
    return `${days}j${hours > 0 ? ` ${hours}h` : ""}`;
  }
  if (hours > 0) {
    return `${hours}h`;
  }
  return `${Math.max(1, totalMinutes)} min`;
}



export const ChatPage = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const activeClientId = useOrgStore((s) => s.activeClientId);
  const isAllProsMode = activeClientId === ALL_CLIENTS_KEY;
  const { data: organizations = [] } = useOrganizations();
  const { data: conversations, isLoading } = usePortalConversations({
    isOnChatList: true,
  });
  const [subject, setSubject] = useState<string>("");
  const [selectedOrgId, setSelectedOrgId] = useState<string>("");
  const [filter, setFilter] = useState<"ALL" | "TO_TREAT" | "CLOSED">("TO_TREAT");

  const needsExplicitPro =
    isAllProsMode && organizations.length > 1;

  useEffect(() => {
    if (!needsExplicitPro) setSelectedOrgId("");
  }, [needsExplicitPro]);

  useEffect(() => {
    if (
      selectedOrgId &&
      !organizations.some((o) => o.orgId === selectedOrgId)
    ) {
      setSelectedOrgId("");
    }
  }, [organizations, selectedOrgId]);

  const startConversation = useMutation({
    mutationFn: (payload: { subject: string; orgId?: string }) =>
      createPortalConversation(payload.subject, payload.orgId),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: ["portal-conversations"] });
      void navigate(`/chat/${res.id}`);
    },
  });

  const handleStart = (event: FormEvent) => {
    event.preventDefault();
    const trimmed = subject.trim();
    if (trimmed.length < CHAT_THREAD_SUBJECT_MIN_LENGTH) return;
    if (isAllProsMode && organizations.length === 0) return;
    let orgId: string | undefined;
    if (isAllProsMode) {
      if (organizations.length === 1) {
        orgId = organizations[0]!.orgId;
      } else if (needsExplicitPro) {
        if (!selectedOrgId) return;
        orgId = selectedOrgId;
      }
    }
    startConversation.mutate({ subject: trimmed, orgId });
  };

  const canStartThread =
    subject.trim().length >= CHAT_THREAD_SUBJECT_MIN_LENGTH &&
    !(isAllProsMode && organizations.length === 0) &&
    (!needsExplicitPro || !!selectedOrgId);

  const isConversationToTreat = (conversation: NonNullable<typeof conversations>[number]) => {
    if (conversation.status === "CLOSED") return false;
    if (conversation.hasUnread) return true;
    const lm = conversation.lastMessage;
    const effectiveClientId =
      activeClientId === ALL_CLIENTS_KEY
        ? conversation.clientId
        : activeClientId;
    if (!lm || !effectiveClientId) return false;
    // En attente de réponse du praticien : le dernier message est le nôtre
    return lm.senderClientId === effectiveClientId;
  };

  const filteredConversations = (conversations ?? []).filter((conversation) => {
    if (filter === "TO_TREAT") return isConversationToTreat(conversation);
    if (filter === "CLOSED") return conversation.status === "CLOSED";
    return true;
  });

  useEffect(() => {
    if (!conversations?.length) return;
    void postPortalConversationsSeenAll()
      .then(() => {
        void queryClient.invalidateQueries({ queryKey: ["portal-conversations"] });
      })
      .catch(() => {
        // Best effort
      });
  }, [conversations?.length, queryClient]);

  const practitionerChoice = needsExplicitPro ? (
    <div className="space-y-2">
      <Label className="text-foreground text-sm font-medium">
        {t("chat.practitionerForThread", "Praticien")}
      </Label>
      <div className="flex flex-wrap gap-2">
        {organizations.map((org) => (
          <Button
            key={org.orgId}
            type="button"
            size="sm"
            variant={selectedOrgId === org.orgId ? "default" : "outline"}
            onClick={() => setSelectedOrgId(org.orgId)}
          >
            {org.orgName}
          </Button>
        ))}
      </div>
    </div>
  ) : null;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">
          {t("chat.title", "Messages")}
        </h1>
        <p className="text-muted-foreground">
          {t("chat.subtitle", "Échangez avec votre praticien.")}
        </p>
      </div>

      <section className="rounded-lg border">
        {isLoading ? (
          <div className="text-muted-foreground p-4 text-sm">
            {t("common.loading", "Chargement...")}
          </div>
        ) : !conversations || conversations.length === 0 ? (
          <div className="text-muted-foreground flex flex-col items-center gap-4 p-4 text-center text-sm">
            <LottieIllustration
              animationData={ghibliLottie}
              size={200}
              className="rounded-md bg-white/90 p-1 opacity-90 dark:bg-white/0"
            />
            <div className="flex items-center justify-center gap-2">
              <MessageSquareText className="h-4 w-4 shrink-0" />
              <p className="max-w-2xl">
                {t(
                  "chat.empty",
                  "Vous n’avez pas encore de discussion. Indiquez un sujet puis écrivez à votre praticien.",
                )}
              </p>
            </div>
            <form onSubmit={handleStart} className="flex flex-col gap-2">
              {practitionerChoice}
              <label className="text-foreground text-sm font-medium">
                {t("chat.threadSubject", "Sujet de la demande")}
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) =>
                  setSubject(e.target.value.slice(0, CHAT_THREAD_SUBJECT_MAX_LENGTH))
                }
                maxLength={CHAT_THREAD_SUBJECT_MAX_LENGTH}
                placeholder={t(
                  "chat.threadSubjectPlaceholder",
                  "Ex. : question sur mon dernier rendez-vous",
                )}
                className="border-input bg-background focus-visible:ring-ring h-14 w-full rounded-md border px-3 text-lg outline-none focus-visible:ring-2 md:h-10 md:text-sm"
              />
              <Button
                type="submit"
                disabled={startConversation.isPending || !canStartThread}
                className="h-14 text-lg md:h-9 md:text-sm"
              >
                {startConversation.isPending
                  ? t("common.loading", "Chargement...")
                  : t("chat.startConversation", "Écrire à mon praticien")}
              </Button>
              {startConversation.isError ? (
                <p className="text-destructive mt-2 text-xs">
                  {t(
                    "chat.startConversationError",
                    "Impossible d’ouvrir la discussion. Réessayez plus tard.",
                  )}
                </p>
              ) : null}
            </form>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 border-b px-4 py-2">
              <Button
                type="button"
                size="sm"
                variant={filter === "ALL" ? "default" : "outline"}
                onClick={() => setFilter("ALL")}
              >
                Tous
              </Button>
              <Button
                type="button"
                size="sm"
                variant={filter === "TO_TREAT" ? "default" : "outline"}
                onClick={() => setFilter("TO_TREAT")}
              >
                À traiter
              </Button>
              <Button
                type="button"
                size="sm"
                variant={filter === "CLOSED" ? "default" : "outline"}
                onClick={() => setFilter("CLOSED")}
              >
                Clôturés
              </Button>
            </div>
            <ul className="divide-y">
              {filteredConversations.map((conversation) => (
                (() => {
                  const remainingMs = getRemainingBeforeAutoArchive(conversation.updatedAt);
                  const showCountdown =
                    conversation.status !== "CLOSED" && remainingMs > 0;
                  const isCountdownWarning = showCountdown && remainingMs <= WARNING_THRESHOLD_MS;

                  return (
                    <li key={conversation.id}>
                      <Link
                        to={`/chat/${conversation.id}`}
                        className="hover:bg-muted/45 block px-4 py-3 transition-colors"
                      >
                        <div className="flex items-end justify-between gap-4">
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium">
                              {conversation.subject?.trim()
                                ? conversation.subject
                                : (conversation.peerLabel ??
                                  `Conversation ${conversation.id.slice(0, 8)}`)}
                            </p>
                            {conversation.org?.name ? (
                              <p className="text-muted-foreground truncate text-[11px]">
                                {conversation.org.name}
                              </p>
                            ) : null}
                          </div>
                          <div className="flex shrink-0 flex-col items-end gap-0.5 text-right">
                            <span className="text-muted-foreground text-xs leading-none tabular-nums">
                              {new Date(conversation.updatedAt).toLocaleString(locale)}
                            </span>
                            {showCountdown ? (
                              <span
                                className={
                                  isCountdownWarning
                                    ? "text-destructive max-w-[min(100%,14rem)] text-[11px] leading-snug"
                                    : "text-muted-foreground max-w-[min(100%,14rem)] text-[11px] leading-snug"
                                }
                              >
                                Archivage auto dans {formatRemainingBeforeAutoArchive(remainingMs)}
                              </span>
                            ) : null}
                          </div>
                        </div>
                        <p className="text-muted-foreground mt-1 line-clamp-1 text-sm">
                          {conversation.lastMessage?.content ??
                            t("chat.noMessage", "Aucun message")}
                        </p>
                        {isConversationToTreat(conversation) ? (
                          <span className="text-primary mt-1 inline-flex text-[11px] font-medium">
                            À traiter
                          </span>
                        ) : null}
                      </Link>
                    </li>
                  );
                })()
              ))}
            </ul>
            {filteredConversations.length === 0 ? (
              <div className="text-muted-foreground border-t px-4 py-6 text-center text-sm">
                <div className="flex flex-col items-center justify-center gap-2">
                  <LottieIllustration
                    animationData={ghibliLottie}
                    size={200}
                    className="rounded-md bg-white/90 p-1 opacity-90 dark:bg-white/0"
                  />
                  <p>Aucun fil dans ce filtre.</p>
                </div>
              </div>
            ) : null}
            <form
              onSubmit={handleStart}
              className="border-t p-4 flex flex-col gap-2"
            >
              {practitionerChoice}
              <label className="text-foreground text-sm font-medium">
                {t("chat.newThreadSubject", "Nouvelle discussion — sujet")}
              </label>
              <div className="flex flex-col gap-2 sm:flex-row">
                <input
                  type="text"
                  value={subject}
                  onChange={(e) =>
                    setSubject(e.target.value.slice(0, CHAT_THREAD_SUBJECT_MAX_LENGTH))
                  }
                  maxLength={CHAT_THREAD_SUBJECT_MAX_LENGTH}
                  placeholder={t(
                    "chat.threadSubjectPlaceholder",
                    "Ex. : question sur mon dernier rendez-vous",
                  )}
                  className="border-input bg-background focus-visible:ring-ring h-14 w-full flex-1 rounded-md border px-3 text-lg outline-none focus-visible:ring-2 sm:w-auto md:h-10 md:text-sm"
                />
                <Button
                  type="submit"
                  disabled={startConversation.isPending || !canStartThread}
                  className="h-14 shrink-0 text-lg md:h-9 md:text-sm"
                >
                  {startConversation.isPending
                    ? t("common.loading", "Chargement...")
                    : t("chat.startNewThread", "Ouvrir un fil")}
                </Button>
              </div>
            </form>
          </>
        )}
      </section>
    </div>
  );
}
